"use client"
import { useEffect, useState } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { useRemote } from "@/lib/api-client"
import { UiScope } from "@/components/ui/animated/provider"
import { SupportWidget } from "@/components/blocks/support-widget/support-widget"
import { FloatingSupportWidget } from "./support-widget"
import { ContactChannels } from "./contact-channels"
import { useChatbotPresentation } from "./use-chatbot-presentation"
import { publicSupportAccessEnded } from "./public-support-access"
import "./communications.css"

function PublicContactWidget({ locale, startOpen, onOpenChange }: { locale: string; startOpen: boolean; onOpenChange: (open: boolean) => void }) {
  const [open, setOpen] = useState(startOpen)
  const presentation = useChatbotPresentation(open)
  const es = locale === "es"
  return <UiScope className="danaro-support-site"><SupportWidget floating chatEnabled={false} startOpen={startOpen} onOpenChange={value => { setOpen(value); onOpenChange(value) }}
    welcome={{ title: es ? "¿Cómo podemos ayudarte?" : "How can we help?", message: es ? "Contacta a Danaro" : "Contact Danaro", starters: [] }}
    homeSupplement={<ContactChannels contact={presentation.contact} locale={locale} />} /></UiScope>
}

export function PublicSupportWidget({ aiEnabled, previewMode, locale }: { aiEnabled: boolean; previewMode: boolean; locale: string }) {
  const [accessEnded, setAccessEnded] = useState(false)
  const [open, setOpen] = useState(false)
  const search = useSearchParams()
  const router = useRouter()
  const previewVisible = search.has("danaro-test") && search.get("danaro-test") !== "off"
  // Layout props may survive client navigation from a normal page to preview.
  // Always resolve access for an explicit preview URL, with its own SWR key.
  const checkAccess = previewVisible || aiEnabled && !previewMode
  const channel = useRemote<{ enabled: boolean; mode?: string }>(checkAccess ? `/api/booking/channel/${previewVisible ? "?view=preview" : ""}` : null, { refreshInterval: 5000, dedupingInterval: 5000, revalidateOnFocus: true })
  const needsPreview = channel.data?.mode ? channel.data.mode === "admin_preview" : previewMode
  const active = (channel.data?.enabled ?? aiEnabled) && (!needsPreview || previewVisible) && !accessEnded
  const [appliedChannel,setAppliedChannel]=useState(channel.data)
  if(appliedChannel!==channel.data){setAppliedChannel(channel.data);if(channel.data?.enabled)setAccessEnded(false)}
  const refreshChannel=channel.mutate
  useEffect(() => {
    const closeConversation = () => { setAccessEnded(true); void refreshChannel().then(data => { if (data?.enabled) setAccessEnded(false) }).catch(() => {}) }
    window.addEventListener(publicSupportAccessEnded, closeConversation)
    return () => window.removeEventListener(publicSupportAccessEnded, closeConversation)
  }, [refreshChannel])
  useEffect(() => {
    if (!previewMode || !previewVisible) return
    const keepPreview = (event: MouseEvent) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      const link = (event.target as Element | null)?.closest?.("a[href]")
      if (!(link instanceof HTMLAnchorElement) || link.target || link.download || link.origin !== window.location.origin || link.getAttribute("href")?.startsWith("#") || /^\/(?:api|crm|my|document|group-pay)(?:\/|$)/.test(link.pathname)) return
      const target = new URL(link.href)
      if (target.searchParams.has("danaro-test")) return
      target.searchParams.set("danaro-test", "")
      event.preventDefault(); event.stopPropagation()
      router.push(target.pathname + target.search + target.hash)
    }
    document.addEventListener("click", keepPreview, true)
    return () => document.removeEventListener("click", keepPreview, true)
  }, [previewMode, previewVisible, router])
  return active && channel.data?.enabled !== false ? <FloatingSupportWidget surface="site" onOpenChange={setOpen} /> : <PublicContactWidget locale={locale} startOpen={accessEnded || open} onOpenChange={setOpen} />
}
